import { cx } from '@linaria/core';
import { useSearchParams } from 'next/navigation';

import { SubHeadline } from '~components/Typography';
import { useRetainedValue } from '~hooks/useRetainedValue';
import { useTranslations } from '~i18n/hooks';
import { usePathname, useRouter } from '~i18n/navigation';
import { CardButton } from '~shared/ui/organisms/CardButton';
import { updateQueryParamsWithPathname } from '~utils/searchParams';
import { ScrollWithSelected } from '~widgets/BotWizard/ui/ScrollWithSelected';

import type {
	BotWizardFieldImage,
	BotWizardFieldNames,
	BotWizardValues,
} from '../../model/types';
import { getDependencyContent } from '../../model/utils';
import { FieldButton } from '../FieldButton';
import { WorkingArea } from '../styles';
import {
	ExampleMessage,
	ExampleWrapper,
	ImagesWrapper,
	StyledCardButton,
} from './styles';

export const FieldImage = ({
	id,
	data,
	contentDependency,
	values,
	queryKey,
	openModal,
	customValues,
	allowCustom,
	multiSelect,
	withExample,
	layout,
}: BotWizardFieldImage & {
	id: BotWizardFieldNames;
	values: BotWizardValues;
	openModal: () => void;
	customValues: { [key in BotWizardFieldNames]?: string };
}) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const router = useRouter();
	const t = useTranslations('BotWizard');

	const localData = getDependencyContent({
		data,
		contentDependency,
		values,
	});

	const [value, setValue] = useRetainedValue(
		values[id] as string | undefined,
		(v) => {
			router.push(
				updateQueryParamsWithPathname({ [queryKey]: v }, pathname, searchParams)
			);
		}
	);
	// TODO support multiselect
	if (multiSelect) return null;
	if (!localData) return null;
	const images = localData.filter((el) => el.image) || [];
	const texts = localData.filter((el) => !el.image) || [];
	return (
		<>
			{images.length > 0 && layout.type === 'wrap' && (
				<WorkingArea>
					<ImagesWrapper className={cx(`option${layout.option}`)}>
						{images.map((el) => (
							<CardButton
								onClick={() => setValue(el.id)}
								quality={90}
								priority={false}
								width={300}
								height={300}
								aspectRatio={layout.aspectRatio || 'vertical'}
								desktopAspectRatio={layout.desktopAspectRatio}
								selected={value === el.id}
								key={el.id}
								text={el.text}
								image={el.image!}
								item={el.media}
							/>
						))}
					</ImagesWrapper>
				</WorkingArea>
			)}
			{images.length > 0 && layout.type === 'carousel' && (
				<ScrollWithSelected
					array={images.map((el) => ({ ...el, key: el.id }))}
					selected={(el) => {
						return value === el.id;
					}}
					template={(el, selected) => (
						<StyledCardButton
							onClick={() => setValue(el.id)}
							className={cx(`option${layout.option}`)}
							width={200}
							height={200}
							priority={false}
							quality={90}
							text={el.text}
							image={el.image!}
							selected={selected}
							item={el.media}
						/>
					)}
				/>
			)}
			{texts.length > 0 && (
				<FieldButton
					allowCustom={allowCustom}
					customValues={customValues}
					openModal={openModal}
					title=""
					previewTitle=""
					queryKey={queryKey}
					type="button"
					data={texts}
					id={id}
					values={values}
				/>
			)}
			{withExample && (
				<ExampleWrapper>
					<SubHeadline color="grey-52-solid" size="M">
						{t('chatExampleTitle')}
					</SubHeadline>
					{localData
						.find((el) => el.id === values[id])
						?.example?.map((el, index) => (
							<ExampleMessage align={'left'} size="M" key={index}>
								{el}
							</ExampleMessage>
						))}
				</ExampleWrapper>
			)}
		</>
	);
};
